/* eslint-disable test-selectors/button */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Tooltip } from 'antd';
import { debounce } from 'lodash';
import styled from '@emotion/styled';
import useWhatsChanging from 'vl-common/src/hooks/useWhatsChanging';
import VideoMenu from '@components/VideoMenu/VideoMenu';
import { RoomDataProvider, useRoomData } from '@lib/context/Room';
import { MdCancel, MdMicOff, MdModeEdit, MdVideocamOff } from 'react-icons/md';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useDispatch, useSelector } from 'react-redux';
import { useComponentSize } from 'react-use-size';
import SignalQuality from '@components/Video/SignalQuality';
import DismissibleAlert from '@components/UI/DismissibleAlert';
import useMyRole from '@hooks/useMyRole';
import Twilio, {
  LocalAudioTrack,
  LocalVideoTrack,
  type NetworkQualityLevel,
  type NetworkQualityStats,
  Room,
  createLocalAudioTrack,
  createLocalVideoTrack
} from 'twilio-video';
import { useRuntimeConfig } from '@vl-core/hooks/useConfig';
import getObjectId from '@lib/getObjectId';
import { AiFillEye, AiFillEyeInvisible, AiFillPhone } from 'react-icons/ai';
import { IoIosDownload } from 'react-icons/io';
import { useRouter } from 'next/router';
import * as actions from '@actions';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import useUser from 'vl-core/hooks/useUser';
import { css } from '@emotion/react';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';
import { stopLocalParticipant } from '@hooks/useStopTwilioOnUnmount';
import useSessionStorage from 'vl-common/src/hooks/useSessionStorage';
import useIsClinician from 'vl-common/src/hooks/useIsClinician';
import { Appointment } from 'vl-common/src/schemas/shared';
import { Lobby } from '@lib/Lobby';
import useSendTwilioEvents from '@hooks/useSendTwilioEvents';
import { useTranslation } from 'react-i18next';
import ReconnectingVideoOverlay from './ReconnectingVideoOverlay';

export function useVideoTrackDimensions(track?: Twilio.VideoTrack) {
  const [dimensions, setDimensions] = useState({ height: track?.dimensions.height, width: track?.dimensions.width });

  // subscribe to track's dimensionsChanged event
  useEffect(() => {
    if (track) {
      const handleDimensionsChanged = ({ dimensions }: Twilio.VideoTrack) => setDimensions({ ...dimensions });

      // just in case the track dimensions have changed since mounting and before getting to here
      setDimensions(track.dimensions);
      track.on('dimensionsChanged', handleDimensionsChanged);
      return () => {
        track.off('dimensionsChanged', handleDimensionsChanged);
      };
    }

    return () => {};
  }, [track]);

  return dimensions;
}

export function useParticipants(room?: Room) {
  const [participants, setParticipants] = useState<Twilio.Participant[]>([]);
  const [reconnectingParticipants, setReconnectingParticipants] = useState(new Set());

  useLayoutEffect(() => {
    if (!room) {
      setParticipants([]);
      return () => {};
    }

    const syncParticipants = () => {
      setParticipants((participants) => {
        const newParticipants = Array.from(room.participants.values()).filter((p) => p);

        if (participants.length !== newParticipants.length) return newParticipants;

        if (!participants.every((p, i) => p === newParticipants[i])) return newParticipants;

        return participants;
      });
    };

    syncParticipants();

    const participantConnected = (participant: Twilio.Participant) =>
      setParticipants((prevParticipants) => [...prevParticipants, participant]);

    const participantReconnecting = (participant: Twilio.Participant) => {
      setReconnectingParticipants((prevParticipants) => {
        const newSet = new Set(prevParticipants);
        newSet.add(participant.sid);
        return newSet;
      });
    };

    const participantReconnected = (participant: Twilio.Participant) =>
      setReconnectingParticipants((prevParticipants) => {
        const newSet = new Set(prevParticipants);
        newSet.delete(participant.sid);
        return newSet;
      });

    const participantDisconnected = (participant: Twilio.Participant) => {
      setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== participant));
      setReconnectingParticipants((prevParticipants) => {
        const newSet = new Set(prevParticipants);
        newSet.delete(participant.sid);
        return newSet;
      });
    };

    room.on('participantConnected', participantConnected);
    room.on('participantReconnecting', participantReconnecting);
    room.on('participantReconnected', participantReconnected);
    room.on('participantDisconnected', participantDisconnected);
    room.on('reconnected', syncParticipants);

    return () => {
      room.off('participantConnected', participantConnected);
      room.off('participantReconnecting', participantReconnecting);
      room.off('participantReconnected', participantReconnected);
      room.off('participantDisconnected', participantDisconnected);
      room.off('reconnected', syncParticipants);
    };
  }, [room]);
  return {
    localParticipant: room?.localParticipant,
    remoteParticipants: participants,
    reconnectingParticipants: reconnectingParticipants
  };
}

export function useTrack(
  publication: Twilio.VideoTrackPublication | Twilio.AudioTrackPublication,
  trackEnabled = (kind?: string) => {},
  trackDisabled = (kind?: string) => {}
) {
  const [track, setTrack] = useState(publication?.track);

  useEffect(() => {
    // Reset the track when the 'publication' variable changes.
    setTrack(publication?.track);

    if (!publication) {
      return () => {};
    }

    const removeTrack = () => setTrack(null);

    publication.on('subscribed', setTrack);
    publication.on('unsubscribed', removeTrack);
    return () => {
      publication.off('subscribed', setTrack);
      publication.off('unsubscribed', removeTrack);
    };
  }, [publication]);

  useEffect(() => {
    function handleEnabled() {
      trackEnabled(track.kind);
    }
    function handleDisabled() {
      trackDisabled(track.kind);
    }

    if (track) {
      track.on('enabled', handleEnabled);
      track.on('disabled', handleDisabled);

      if (track.isEnabled) {
        handleEnabled();
      } else {
        handleDisabled();
      }
    }

    return () => {
      track?.off('enabled', handleEnabled);
      track?.off('disabled', handleDisabled);
    };
  }, [track]);

  return track;
}

export function useValueList() {
  const [values] = useState(() => new Set());
  const [list, setList] = useState([]);

  const add = useCallback((e) => {
    if (values.has(e)) return;
    values.add(e);
    setList((list) => [...list, e]);
  }, []);

  const remove = useCallback((e) => {
    if (!values.has(e)) return;
    values.delete(e);
    setList((list) => list.filter((item) => item !== e));
  }, []);

  const reset = useCallback((newValues) => {
    setList((oldList) => {
      function update() {
        values.clear();
        newValues.forEach((v) => values.add(v));
        return [...newValues];
      }
      if (oldList.length !== newValues.length) return update();
      if (!newValues.every((n) => values.has(n))) return update();

      return oldList;
    });
  }, []);

  const filter = useCallback((predicate) => {
    const oldSize = values.size;

    values.forEach((v) => {
      if (!predicate(v)) {
        values.delete(v);
      }
    });
    if (oldSize !== values.size) {
      setList(Array.from(values.values()));
    }
  }, []);

  return {
    list,
    add,
    remove,
    reset,
    filter
  };
}

export function usePublications(participant: Twilio.Participant, logger) {
  const { list: publications, reset, add, remove, filter } = useValueList();
  useEffect(() => {
    if (!participant) return () => {};

    // Reset the publications when the 'participant' variable changes.
    reset(Array.from(participant.tracks.values()).filter((p) => p && p.kind !== 'data'));

    const trackStopped = (track: Twilio.TrackPublication) => {
      console.log('track stopped', track);
      filter((p) => p.track !== track);
    };

    participant.on('trackPublished', add);
    participant.on('trackUnpublished', remove);
    participant.on('trackStopped', trackStopped);

    return () => {
      participant.off('trackPublished', add);
      participant.off('trackUnpublished', remove);
      participant.off('trackStopped', trackStopped);
    };
  }, [logger, participant]);

  return publications as (Twilio.AudioTrackPublication | Twilio.VideoTrackPublication)[];
}

function AudioTrack({ track, logger }) {
  const element = useRef(undefined);

  useEffect(() => {
    if (!track) return () => {};

    track.attach(element.current);
    return () => track.detach(element.current);
  }, [logger, track]);

  return <audio ref={element} />;
}

const Video = styled.video`
  height: 100%;
  width: 100%;
  border-radius: 25px;
  object-fit: cover;
`;

const PreviewVideoContainer = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  width: 100%;
`;

const PreviewVideo = styled(Video)`
  border-radius: 25px;
`;

const PreviewEditControlsContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 32px;
  z-index: 3;
  display: flex;
  direction: rtl;
`;

const TopRightControls = styled(PreviewEditControlsContainer)`
  // High Contrast Fix
  body[data-contrast='high'] & {
    & svg path[fill='none'] {
      fill: none !important;
    }
  }
  top: 0;
  right: 0;
`;

const ButtonContainer = styled.div`
  width: 32px;
  height: 32px;
  margin-left: 2px;
  margin-right: 2px;
`;

const PreviewButton = styled(Button)`
  height: 32px;
  width: 32px;
`;

interface VideoTrackProps {
  track: Twilio.VideoTrack;
  logger: typeof console.log;
  repositionModeEnabled: boolean;
  showPreview: boolean;
  hideVideoPreview: boolean;
  onDimensionsChanged: (dimensions: any) => void;
  showPreviewPositions: boolean;
  hidePreviewPositions: boolean;
  room: Twilio.Room;
}
function VideoTrack({
  track,
  logger,
  repositionModeEnabled,
  showPreview,
  hideVideoPreview,
  showPreviewPositions = () => {},
  hidePreviewPositions = () => {},
  onDimensionsChanged = (dimensions) => {},
  room
}: VideoTrackProps) {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { roomData } = useRoomData();
  const myRole = useMyRole();
  const [idHasBeenRecorded, setIdhasBeenRecorded] = useState(false);
  const [isTwilioIdSent, setIsTwilioIdSent] = useSessionStorage(roomData?.roomName, false);

  const dimensions = useVideoTrackDimensions(track);
  const element = useRef(undefined);
  const isLocalVideo = track instanceof LocalVideoTrack;
  const isClinicianTestCall = new URLSearchParams(window.location.search).has('clinicianTestCall');

  const { reconnectingParticipants } = useParticipants(room);
  const isRemoteReconnecting = reconnectingParticipants.size > 0;

  useEffect(() => {
    onDimensionsChanged(dimensions);
  }, [dimensions, onDimensionsChanged]);

  const sendCallID = debounce(
    (user_guid, appointment_id, twilio_id, call_type) => sendTwilioID(user_guid, appointment_id, twilio_id, call_type),
    2000
  );

  const sendTwilioID = async (user_guid, appointment_id, twilio_id, call_type) => {
    if (isTwilioIdSent || isClinicianTestCall) return;
    await dispatch(actions.sendTwilioID({ user_guid, appointment_id, twilio_id, call_type })).catch((err) =>
      console.error('sendCallID fails in SimpleVideo', err)
    );
    await dispatch(
      actions.sendCallID({ appt_id: appointment_id, ext_call_id: twilio_id, call_type_code: 'VIDEO' })
    ).catch((err) => console.error('sendCallID fails in SimpleVideo', err));

    setIsTwilioIdSent(true);
  };

  useEffect(() => {
    const unMuted = () => {
      console.log(`%cHa! detected unmuted ${track.kind} track`, 'color: green');
    };

    if (element.current && track) {
      // Only send Twilio ID when the remote track is connected (Can see other participant)
      if (!isLocalVideo && myRole === 'clinician' && !idHasBeenRecorded) {
        setIdhasBeenRecorded(true);
        sendCallID(user?.user_guid, roomData?.appointment_id, roomData?.roomName, roomData?.call_type);
      }
      const { mediaStreamTrack } = track;
      track.attach(element.current);
      mediaStreamTrack.addEventListener('unmute', unMuted);
      return () => {
        track.detach(element.current);
        mediaStreamTrack.removeEventListener('unmute', unMuted);
      };
    }

    return () => {};
  }, [logger, track, user?.user_guid, roomData?.appointment_id, roomData?.roomName, roomData?.call_type]);

  useEffect(() => {
    function unMuted() {
      console.log(`%cHa! detected unmuted ${track.kind} track`, 'color: green');
    }

    if (track) {
      const { mediaStreamTrack } = track;
      mediaStreamTrack.addEventListener('unmute', unMuted);
      return () => mediaStreamTrack.removeEventListener('unmute', unMuted);
    }

    return () => {};
  }, [track]);

  const CancelButton = () => (
    <PreviewButton
      icon={<MdCancel />}
      type="primary"
      shape="circle"
      data-testid="cancel"
      aria-label={t('Cancel position editing')}
    />
  );
  const EditButton = () => (
    <PreviewButton
      icon={<MdModeEdit />}
      type="primary"
      shape="circle"
      data-testid="edit"
      aria-label={t('Edit video position')}
    />
  );
  const HidePreviewButton = () => (
    <PreviewButton
      icon={<AiFillEyeInvisible />}
      type="primary"
      shape="circle"
      data-testid="hide"
      aria-label={t('Hide video')}
    />
  );

  const Preview = () => (
    <>
      <TopRightControls>
        {showPreview && (
          <ButtonContainer onClick={repositionModeEnabled ? hidePreviewPositions : showPreviewPositions}>
            {repositionModeEnabled ? <CancelButton /> : <EditButton />}
          </ButtonContainer>
        )}
        {repositionModeEnabled && (
          <ButtonContainer onClick={hideVideoPreview}>
            <HidePreviewButton />
          </ButtonContainer>
        )}
      </TopRightControls>
      <PreviewVideoContainer>
        {showPreview && <PreviewVideo ref={element} className="local-video-element" />}
      </PreviewVideoContainer>
    </>
  );

  // For Video, muted={true} ensures that the video starts cleanly!
  const RemoteVideo = () => {
    return (
      <div>
        {isRemoteReconnecting && <ReconnectingVideoOverlay />}
        <Video ref={element} className="remote-video-element" muted={true} />
      </div>
    );
  };

  return isLocalVideo ? Preview() : RemoteVideo();
}

const LocalPreviewContainer = styled.div<{
  fullPage: boolean;
}>`
  position: relative;
  border: 1px solid lightgrey;
  border-radius: 25px;
  flex-grow: 1;
  flex-shrink: 1;
  width: 100%;
  padding: 8px;

  ${(props) => {
    return (
      props.fullPage &&
      `
        position: fixed;
        width: 100vw;
        height: auto;
        top: 0;
        left: 0;
        z-index: 1;
      `
    );
  }};
`;

const QuadrantInstructions = styled.div<{
  showInstructions: boolean;
}>`
  background-color: rgb(175, 175, 174);
  border-radius: 25px;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  text-align: center;
  display: ${({ showInstructions }) => (showInstructions ? 'flex' : 'none')};

  p {
    margin: 0 10px;
  }
`;

const QuadrantContainer = styled(UnstyledButton)`
  padding: 8px;
  z-index: 2;
  position: absolute;
  width: auto;
  height: auto;
  aspect-ratio: 4 / 3;
  max-width: 40%;
  min-width: 100px;
  min-height: 100px;
  opacity: 1;
  overflow: hidden;

  @media screen and (max-width: 600px) {
    width: 50%;
  }
`;

const RemoteParticipant = styled.div<{ isTestMode?: boolean; testUserRole: string }>`
  position: relative;
  width: ${({ isTestMode }) => (isTestMode ? 'auto' : '100%')};
  height: ${({ isTestMode }) => (isTestMode ? '260px' : 'auto')};
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 25px;
  display: ${({ isTestMode }) => (isTestMode ? 'flex' : 'block')};
  background-image: ${({ isTestMode, testUserRole }) => {
    if (!isTestMode) return 'none';
    if (isTestMode && testUserRole === 'patient') return 'url("/static/images/userPersonas/testPatientPersona.png")';
    if (isTestMode && testUserRole === 'clinician') return 'url("/static/images/userPersonas/clin_headshot.png")';
  }};
  background-size: ${({ isTestMode, testUserRole }) => (isTestMode && testUserRole === 'patient' ? 'cover' : 'auto')};
  background-repeat: no-repeat;
  background-position: center;
  div {
    width: 100%;
    height: 100%;
  }
  .remote-video-element {
    position: absolute;
    inset: 0;
  }
`;

const BottomRightControls = styled(PreviewEditControlsContainer)`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 10;
  margin: 8px;
`;

const ShowPreviewButton = styled(() => <PreviewButton icon={<AiFillEye />} type="primary" shape="circle" />)`
  width: 32px;
  height: 32px;
`;

type PreviewPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

function Participants({
  localPreview,
  repositionModeEnabled,
  hidePreviewPositions = () => {},
  showVideoPreview,
  children,
  fullPage,
  overlayMessages,
  alert,
  showPreview,
  isTestMode = false
}) {
  const { t } = useTranslation('common');
  const [previewPosition, setPreviewPosition] = useState<PreviewPosition>('bottom-right');
  const isClinician = useIsClinician();

  const possiblePreviewPositions: Record<PreviewPosition, React.CSSProperties> = {
    'top-left': { top: 0, left: 0 },
    'top-right': { top: 0, right: 0 },
    'bottom-left': { bottom: 0, left: 0 },
    'bottom-right': { bottom: 0, right: 0 }
  } as const;

  const onChangePreviewPosition = (position: PreviewPosition) => {
    if (repositionModeEnabled) {
      setPreviewPosition(position);
      hidePreviewPositions();
    }
  };

  return (
    <LocalPreviewContainer fullPage={fullPage}>
      <RemoteParticipant
        className="remote-stage"
        isTestMode={isTestMode}
        testUserRole={isClinician ? 'patient' : 'clinician'}
      >
        {children}
      </RemoteParticipant>

      {(Object.keys(possiblePreviewPositions) as PreviewPosition[]).map((key) => {
        const positionStyle = possiblePreviewPositions[key];
        const isActive = showPreview && key === previewPosition;

        return (
          <QuadrantContainer
            key={key}
            style={{ ...positionStyle, height: '30%' }}
            onClick={() => onChangePreviewPosition(key)}
          >
            {isActive && localPreview}
            {!isActive && (
              <QuadrantInstructions showInstructions={repositionModeEnabled}>
                <div style={{ fontSize: '1rem', color: '#ECECEC' }}>
                  <p>
                    <IoIosDownload /> {t('Click here to change')}
                  </p>
                </div>
              </QuadrantInstructions>
            )}
          </QuadrantContainer>
        );
      })}

      {overlayMessages && alert.message && (
        <Alert
          className="login-alert"
          style={{ margin: '1rem 0' }}
          message={alert.message}
          type={alert.type}
          closable
          showIcon
        />
      )}

      {!showPreview && (
        <BottomRightControls>
          <ButtonContainer onClick={showVideoPreview}>
            <ShowPreviewButton />
          </ButtonContainer>
        </BottomRightControls>
      )}
    </LocalPreviewContainer>
  );
}

const StyledParticipants = styled(Participants)`
  @media screen and (max-width: 600px) {
    min-height: 80vh;
  }
`;

interface TrackProps {
  track: Twilio.VideoTrack | Twilio.AudioTrack;
  logger: typeof console.log;
  showPreview: boolean;
  hideVideoPreview: boolean;
  repositionModeEnabled: boolean;
  onDimensionsChanged: (dimensions: any) => void;
  showPreviewPositions: boolean;
  hidePreviewPositions: boolean;
  room: Twilio.Room;
}
function Track({
  track,
  logger,
  showPreview,
  hideVideoPreview,
  repositionModeEnabled,
  showPreviewPositions,
  hidePreviewPositions,
  onDimensionsChanged = (dimensions) => {},
  room
}: TrackProps) {
  useEffect(() => {
    function unMuted() {
      console.log(`%cHa! detected unmuted ${track?.kind} track`, 'color: green');
    }

    if (track) {
      const { mediaStreamTrack } = track as { mediaStreamTrack?: MediaStreamTrack };

      if (mediaStreamTrack && typeof (mediaStreamTrack as any).addEventListener === 'function') {
        (mediaStreamTrack as any).addEventListener('unmute', unMuted);
        return () => {
          (mediaStreamTrack as any).removeEventListener('unmute', unMuted);
        };
      }
    }

    return () => {};
  }, [track]);

  if (!track) return null;

  return (
    <>
      {track.kind === 'video' && (
        <VideoTrack
          track={track}
          logger={logger}
          showPreview={showPreview}
          hideVideoPreview={hideVideoPreview}
          onDimensionsChanged={onDimensionsChanged}
          showPreviewPositions={showPreviewPositions}
          hidePreviewPositions={hidePreviewPositions}
          repositionModeEnabled={repositionModeEnabled}
          room={room}
        />
      )}
      {track.kind === 'audio' && <AudioTrack track={track} logger={logger} />}
    </>
  );
}

interface PublicationProps {
  publication: Twilio.VideoTrackPublication | Twilio.AudioTrackPublication;
  logger: typeof console.log;
  repositionModeEnabled: boolean;
  showPreview: boolean;
  hideVideoPreview: boolean;
  showPreviewPositions: boolean;
  hidePreviewPositions: boolean;
  trackEnabled: (kind: string) => void;
  trackDisabled: (kind: string) => void;
  onDimensionsChanged: (dimensions: any) => void;
  room: Twilio.Room;
}
function Publication({
  publication,
  logger,
  repositionModeEnabled,
  showPreview,
  hideVideoPreview,
  showPreviewPositions,
  hidePreviewPositions,
  trackEnabled = (kind: string) => {},
  trackDisabled = (kind: string) => {},
  onDimensionsChanged = (dimensions) => {},
  room
}: PublicationProps) {
  const track = useTrack(publication, trackEnabled, trackDisabled);

  useEffect(() => {
    console.log('Publication mounts', publication.trackSid);

    return () => console.log('Publication unmounts', publication.trackSid);
  }, []);

  return (
    <Track
      track={track}
      logger={logger}
      showPreview={showPreview}
      hideVideoPreview={hideVideoPreview}
      onDimensionsChanged={onDimensionsChanged}
      showPreviewPositions={showPreviewPositions}
      hidePreviewPositions={hidePreviewPositions}
      repositionModeEnabled={repositionModeEnabled}
      room={room}
    />
  );
}

const ParticipantSignalStrengthSpan = styled.span`
  padding-bottom: 0.35rem;
`;

const ParticipantSignalStrength = ({ participant, onQualityChanged = (network, latency) => {} }) => {
  const [networkQuality, setNetworkQuality] = useState(participant?.networkQualityLevel || 0);
  const [audioLatency, setAudioLatency] = useState(
    participant?.networkQualityStats?.audio?.recvStats?.latency.level || 0
  );
  const [videoLatency, setVideoLatency] = useState(
    participant?.networkQualityStats?.video?.recvStats?.latency.level || 0
  );
  const [attempt, setAttempt] = useState(0);
  const reconnect = () => setAttempt((a) => a + 1);

  useEffect(() => {
    if (!participant) return () => {};

    function logNetworkQualityStats(quality: NetworkQualityLevel, stats: NetworkQualityStats) {
      const audioLatency = stats?.audio?.recvStats?.latency?.level;
      const videoLatency = stats?.video?.recvStats?.latency?.level;

      setNetworkQuality(quality);
      if (audioLatency) setAudioLatency(audioLatency);
      if (videoLatency) setVideoLatency(videoLatency);
      onQualityChanged(quality, Math.max(audioLatency || 0, videoLatency || 0));
    }

    setNetworkQuality(participant.networkQualityLevel);
    participant.on('networkQualityLevelChanged', logNetworkQualityStats);

    return () => participant.off('networkQualityLevelChanged', logNetworkQualityStats);
  }, [participant, attempt]);

  return (
    <ParticipantSignalStrengthSpan onClick={reconnect}>
      {/* @ts-ignore */}
      <SignalQuality height={25} quality={networkQuality} latency={Math.max(audioLatency, videoLatency)} width={40} />
    </ParticipantSignalStrengthSpan>
  );
};

export function Participant({
  participant,
  logger = console.log,
  showPreview,
  hideVideoPreview,
  repositionModeEnabled,
  showPreviewPositions,
  hidePreviewPositions,
  trackEnabled = (kind: string) => {},
  trackDisabled = (kind: string) => {},
  onDimensionsChanged = (dimensions) => {},
  children = [] as React.ReactNode,
  room
}) {
  const publications = usePublications(participant, logger);

  useWhatsChanging(`Participant - ${getObjectId(participant)}`, { publications, participant }, logger);

  return (
    <>
      {publications.map((p) => (
        <Publication
          key={p.trackSid}
          publication={p}
          logger={logger}
          showPreview={showPreview}
          hideVideoPreview={hideVideoPreview}
          trackEnabled={trackEnabled}
          trackDisabled={trackDisabled}
          onDimensionsChanged={onDimensionsChanged}
          showPreviewPositions={showPreviewPositions}
          hidePreviewPositions={hidePreviewPositions}
          repositionModeEnabled={repositionModeEnabled}
          room={room}
        />
      ))}
      {publications.length === 0 || (!publications.some((p) => p.kind === 'video') && children)}
    </>
  );
}

export const VideoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 3px;
  padding-right: 3px;

  .fullscreen {
    width: 100%;
  }

  .fullscreen-enabled {
    background-color: darkgray;
  }

  .full-page-video-menu {
    position: fixed;
    width: 100vw;
    bottom: 0;
    left: 0;
    z-index: 10000;
    justify-content: center;
    background-color: white;
  }

  .normal-video-menu {
    margin: 10px;
    z-index: 1000;
  }
`;

export const VideoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const Resizable = styled.div<{ fullScreen: boolean }>`
  //resize: both;
  resize: none;
  overflow: auto;

  ${({ fullScreen }) =>
    fullScreen &&
    `
      .remote-stage {
        max-width: calc((100vh - 200px) * 4 / 3);
        margin: 0 auto;
      }
    `}
`;

const StatusIcon = styled.span`
  color: red;
  font-size: 30px;
  margin-left: 10px;
  flex-shrink: 0;

  svg {
    vertical-align: top;
  }
`;

export function VideoControls({
  fullPage,
  fullScreen,
  room,
  handleFullscreenRequest,
  onRequestPopout,
  onRequestHangup = () => {},
  localParticipant,
  signalQualityChanged
}) {
  const { t } = useTranslation('common');
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onRequestHangup();
      }
    },
    [onRequestHangup]
  );

  useEffect(() => {
    if (fullScreen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <VideoRow className={fullPage ? 'full-page-video-menu' : 'normal-video-menu'}>
      {room && (
        // @ts-ignore
        <VideoMenu
          room={room}
          onRequestFullScreen={handleFullscreenRequest}
          isPoppedOut={fullScreen}
          onRequestPopout={onRequestPopout}
          fullScreen={fullScreen}
        />
      )}
      <div
        css={{
          marginLeft: '8px',
          marginRight: '8px',
          paddingTop: '1rem',
          paddingBottom: 0
        }}
      >
        <ParticipantSignalStrength participant={localParticipant} onQualityChanged={signalQualityChanged} />
      </div>
      <Tooltip title={<span aria-hidden={true}>{t('Hang up (Esc)')}</span>} trigger={['hover', 'focus']}>
        <Button
          data-testid="hangup-button"
          aria-label={t('Hang up')}
          shape="circle"
          type="primary"
          danger
          css={css`
            font-size: 20px;
            margin: 0;
            padding: 0;

            body:not([data-contrast='high']) &:focus-visible {
              outline: 2px solid var(--productGray) !important;
              outline-offset: 2px;
            }
          `}
          icon={<AiFillPhone />}
          onClick={onRequestHangup}
        />
      </Tooltip>
    </VideoRow>
  );
}

const HudBackground = styled.div`
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 25px;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
`;

const PartyName = styled.div`
  color: var(--productGray);
  font-size: 16.4px;
  font-weight: 500;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 0.5em;
`;

const AppointmentVideo = ({
  children,
  pictureInPicture,
  fullPage,
  fullScreen,
  handleFullscreenRequest,
  localParticipant,
  logger,
  remoteParticipants,
  room,
  roomName,
  onRequestPopout,
  onRequestHangup,
  otherPartyName = 'Mr John Smith',
  overlayMessages = false,
  appointment,
  isTestMode = false
}) => {
  const { t } = useTranslation('common');
  const { NETWORK_LATENCY_WARNING_THRESHOLD, NETWORK_QUALITY_WARNING_THRESHOLD } = useRuntimeConfig();
  const { ref } = useComponentSize();
  const [alert, setAlert] = useState({ message: '', type: 'warning' });
  const { setRoomData } = useRoomData();

  const [remoteAudioState, setRemoteAudioState] = useState('enabled');
  const [remoteVideoState, setRemoteVideoState] = useState('enabled');
  const [repositionModeEnabled, setRepositionModeEnabled] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const settersByKind = { audio: setRemoteAudioState, video: setRemoteVideoState, data: () => {} };
  const isClinicianTestCall = new URLSearchParams(window.location.search).has('clinicianTestCall');
  const handleTrackEnabled = (kind: 'audio' | 'video' | 'data') => {
    settersByKind[kind]('enabled');
  };
  const myRole = useMyRole();
  const router = useRouter();

  useEffect(() => {
    setRoomData({
      roomName,
      appointment_id: appointment?.appointment_id,
      call_type: appointment?.call_type_code?.[0]
    });
  }, [roomName, appointment?.appointment_id, appointment?.call_type_code?.[0]]);

  const handleTrackDisabled = (kind: 'audio' | 'video' | 'data') => {
    settersByKind[kind]('disabled');
  };

  function signalQualityChanged(network, latency) {
    if (network < NETWORK_LATENCY_WARNING_THRESHOLD || latency < NETWORK_QUALITY_WARNING_THRESHOLD) {
      setAlert({ message: 'Your internet connection is poor', type: 'warning' });
    } else {
      setAlert({ message: '', type: 'warning' });
    }
  }

  const handleHangUp = async () => {
    if (myRole === 'patient') {
      if (isTestMode) {
        onRequestHangup();
        stopLocalParticipant(room.localParticipant);
        room?.disconnect();
        return;
      }
      return router.replace('/patients').then();
    }

    confirmationModal({
      title: 'End Video Call',
      content: 'This will finish the video call which then cannot be restarted',
      onOk: async () => {
        await onRequestHangup();
        stopLocalParticipant(room.localParticipant);
        room?.disconnect();
      }
    });

    return true;
  };

  const showPreviewPositions = () => {
    setRepositionModeEnabled(true);
  };

  const hidePreviewPositions = () => {
    setRepositionModeEnabled(false);
  };

  const hideVideoPreview = () => {
    setShowPreview(false);
    hidePreviewPositions();
  };

  const showVideoPreview = () => {
    setShowPreview(true);
    hidePreviewPositions();
  };

  return (
    <Resizable fullScreen={fullScreen}>
      <VideoColumn style={{ width: '100%' }}>
        <VideoRow>
          <DismissibleAlert alert={alert} />
        </VideoRow>
        <VideoRow style={{ alignSelf: 'start', marginBottom: '0.5em', width: 0, minWidth: '100%', height: 32 }}>
          <PartyName title={otherPartyName}>{otherPartyName}</PartyName>
          <span style={{ flexShrink: 0 }}>
            <ParticipantSignalStrength participant={(remoteParticipants || [])[0]} />
          </span>
          {remoteAudioState === 'disabled' && (
            <StatusIcon>
              <MdMicOff />
            </StatusIcon>
          )}
          {remoteVideoState === 'disabled' && (
            <StatusIcon>
              <MdVideocamOff />
            </StatusIcon>
          )}
          {children}
        </VideoRow>
        <VideoRow ref={ref}>
          <StyledParticipants
            fullPage={fullPage}
            localPreview={
              // @ts-ignore
              <Participant
                key={localParticipant?.sid}
                participant={localParticipant}
                logger={logger}
                repositionModeEnabled={repositionModeEnabled}
                showPreviewPositions={showPreviewPositions}
                hidePreviewPositions={hidePreviewPositions}
                showPreview={showPreview}
                hideVideoPreview={hideVideoPreview}
              />
            }
            overlayMessages={overlayMessages}
            repositionModeEnabled={repositionModeEnabled}
            alert={alert}
            hidePreviewPositions={hidePreviewPositions}
            showVideoPreview={showVideoPreview}
            showPreview={showPreview}
            isTestMode={isTestMode}
          >
            {repositionModeEnabled ? <HudBackground /> : null}
            {remoteParticipants.map((p) => {
              let containerStyle: React.CSSProperties;
              if (isTestMode) {
                containerStyle = {
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  backgroundColor: 'transparent',
                  textAlign: 'center'
                };
              } else if (isClinicianTestCall) {
                containerStyle = {
                  height: 250,
                  width: 150,
                  backgroundColor: 'transparent',
                  textAlign: 'center'
                };
              } else {
                containerStyle = {
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'transparent',
                  textAlign: 'center'
                };
              }

              const shouldShowWaitMessage = isTestMode || !isClinicianTestCall;

              return (
                <Participant
                  key={p.sid}
                  participant={p}
                  logger={logger}
                  trackEnabled={handleTrackEnabled}
                  trackDisabled={handleTrackDisabled}
                  // @ts-ignore
                  pictureInPicture={pictureInPicture}
                  showPreviewPositions={showPreviewPositions}
                  hidePreviewPositions={hidePreviewPositions}
                  repositionModeEnabled={repositionModeEnabled}
                  room={room}
                >
                  <div style={containerStyle}>{shouldShowWaitMessage && <>{t('Please wait...')}</>}</div>
                </Participant>
              );
            })}
            {remoteParticipants.length === 0 && <div style={{ width: '100%', height: '100%' }}>&nbsp;</div>}
          </StyledParticipants>
        </VideoRow>
        <VideoControls
          fullPage={fullPage}
          fullScreen={fullScreen}
          room={room}
          handleFullscreenRequest={handleFullscreenRequest}
          onRequestPopout={onRequestPopout}
          onRequestHangup={() => {
            handleHangUp();
          }}
          localParticipant={localParticipant}
          signalQualityChanged={signalQualityChanged}
        />
      </VideoColumn>
    </Resizable>
  );
};

function useInterval(callback: () => void, delay: number, flag: boolean) {
  useEffect(() => {
    if (flag) {
      const intervalId = setInterval(callback, delay);
      return () => {
        clearInterval(intervalId);
      };
    }

    return () => {};
  }, [callback, delay, flag]);
}

interface SimpleVideoProps {
  appointment: Appointment;
  room: Room;
  secondaryToken: unknown;
  sendTokenRequest: () => void;
  open: boolean;
  roomName: string;
  children?: React.ReactNode;
  onRequestHangup: () => void;
  isTestCall?: boolean;
}

export function SimpleVideo({
  appointment,
  room,
  secondaryToken,
  sendTokenRequest,
  open,
  roomName,
  children = null,
  onRequestHangup = () => {},
  isTestCall = false
}: SimpleVideoProps) {
  const appointmentIdentifiers = useMemo(
    () =>
      appointment || {
        appointment_id: 9605,
        clinician_user_guid: '36b7165b-bffd-482a-a901-55c563f1e439',
        patient_user_guid: '5c6f9093-5479-494f-87ee-ff78a8974302',
        status: 'Pending Clinician',
        patient: 'Mr I M Ill',
        clinician: 'Dr I M Not'
      },
    []
  );
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const [pictureInPicture] = useState(false);
  const [fullPage, setFullPage] = useState(false);
  const { localParticipant, remoteParticipants } = useParticipants(room);
  const patientConnectionStatus = remoteParticipants.length === 0 ? '(disconnected)' : '';
  const logger = useCallback((...args: any[]) => {
    console.log(...args);
  }, []);
  const handle = useFullScreenHandle();
  const myRole = useMyRole();
  const { startCall, updatersReady } = useCallStatusUpdaters(appointment);
  const { user_guid } = useUser();
  const sendTwilioEvents = useSendTwilioEvents();

  useEffect(() => {
    if (!room) return () => {};

    return () => {
      Lobby.forRoom(room).leave({ andDisconnect: false }).then();
    };
  }, [room]);

  useEffect(() => {
    if (open) {
      sendTokenRequest();
    }
  }, [sendTokenRequest, open, logger]);

  // while remoteParticipants is empty, send out a START
  useInterval(
    useCallback(() => {
      if (!isTestCall) {
        startCall().then();
      }
    }, [startCall]),
    5 * 1000,
    updatersReady && remoteParticipants.length === 0
  );

  useEffect(() => {
    if (room && updatersReady) {
      startCall().then();
    }
  }, [room, startCall, updatersReady]);

  const remoteParticipantsConnected = remoteParticipants.length > 0;
  useEffect(() => {
    if (remoteParticipantsConnected) {
      sendTwilioEvents({
        userGuid: user_guid,
        appointmentId: appointment.appointment_id,
        event: 'RECEIVING_VIDEO',
        message: JSON.stringify({ from: { sid: remoteParticipants[0].sid } })
      });
    }
  }, [appointment.appointment_id, sendTwilioEvents, remoteParticipantsConnected, user_guid]);

  const handleFullscreenRequest = async (n) => {
    if (remoteParticipants.length === 0) {
      if (handle.active) await handle.exit();
      return;
    }

    if (n === 1) {
      if (handle.active) {
        await handle.exit();
      } else {
        await handle.enter();
      }
    } else {
      setFullPage((fp) => !fp);
    }
  };

  const onRequestPopout = () => {
    setIsPoppedOut((po) => !po);
  };

  return (
    <RoomDataProvider>
      <VideoColumn style={{ width: '100%', maxWidth: 600, margin: '0 auto', minWidth: 320 }}>
        <FullScreen handle={handle}>
          {!isPoppedOut && (
            <AppointmentVideo
              pictureInPicture={pictureInPicture}
              logger={logger}
              room={room}
              localParticipant={localParticipant}
              fullPage={fullPage}
              fullScreen={handle.active}
              roomName={roomName}
              handleFullscreenRequest={handleFullscreenRequest}
              onRequestPopout={onRequestPopout}
              appointment={appointment}
              onRequestHangup={onRequestHangup}
              otherPartyName={
                myRole === 'clinician'
                  ? `${appointmentIdentifiers.patient} ${patientConnectionStatus}`
                  : appointmentIdentifiers.clinician
              }
              remoteParticipants={remoteParticipants}
              overlayMessages={fullPage || isPoppedOut}
            >
              {children}
            </AppointmentVideo>
          )}
        </FullScreen>
      </VideoColumn>
    </RoomDataProvider>
  );
}

// A SimpleVideo variant not attached to an appointment (i.e. a test patient)
export function TestPatientSimpleVideo({
  room,
  otherPartyName,
  onRequestHangup = () => {}
}: {
  room: Room;
  otherPartyName: string;
  onRequestHangup?: () => void;
}) {
  const appointment = useMemo(
    () => ({
      appointment_id: 9605,
      clinician_user_guid: '36b7165b-bffd-482a-a901-55c563f1e439',
      patient_user_guid: '5c6f9093-5479-494f-87ee-ff78a8974302',
      status: 'Pending Clinician',
      patient: 'Mr I M Ill',
      clinician: 'Dr I M Not'
    }),
    []
  );
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const [pictureInPicture] = useState(false);
  const [fullPage, setFullPage] = useState(false);
  const { localParticipant, remoteParticipants } = useParticipants(room);
  const logger = useCallback((...args) => {
    console.log(...args);
  }, []);
  const handle = useFullScreenHandle();

  useEffect(() => {
    let cancelled = false;
    let videoTrackPublish: LocalVideoTrack | null = null;
    let audioTrackPublish: LocalAudioTrack | null = null;

    const publishAV = async () => {
      try {
        const isClinicianTestCall = new URLSearchParams(globalThis.window.location.search).has('clinicianTestCall');
        const isPatientTestCall = globalThis.window.location.pathname.endsWith('/test-call/');

        if (!isClinicianTestCall && !isPatientTestCall) return;

        const [videoTrack, audioTrack] = await Promise.all([createLocalVideoTrack(), createLocalAudioTrack()]);

        if (cancelled) {
          videoTrack.stop();
          audioTrack.stop();
          return;
        }

        videoTrackPublish = videoTrack;
        audioTrackPublish = audioTrack;

        await Promise.all([
          room.localParticipant?.publishTrack(videoTrack),
          room.localParticipant?.publishTrack(audioTrack)
        ]);
      } catch (e) {
        console.error('Failed to start up both audio and video:', e);
      }
    };

    publishAV();

    return () => {
      cancelled = true;
      if (videoTrackPublish && audioTrackPublish) {
        room.localParticipant.unpublishTrack(videoTrackPublish);
        room.localParticipant.unpublishTrack(audioTrackPublish);
        videoTrackPublish.stop();
        audioTrackPublish.stop();
        onRequestHangup();
      }
    };
  }, []);

  // prettier-ignore
  const handleFullscreenRequest = async (n) => {//NOSONAR
    if (remoteParticipants.length === 0) {
      if (handle.active) await handle.exit();
      return;
    }

    if (n === 1) {
      if (handle.active) {
        await handle.exit();
      } else {
        await handle.enter();
      }
    } else {
      setFullPage((fp) => !fp);
    }
  };

  const onRequestPopout = () => {
    setIsPoppedOut((po) => !po);
  };

  return (
    <RoomDataProvider>
      <VideoColumn>
        <FullScreen handle={handle}>
          {!isPoppedOut && (
            <AppointmentVideo
              pictureInPicture={pictureInPicture}
              logger={logger}
              room={room}
              localParticipant={localParticipant}
              fullPage={fullPage}
              fullScreen={handle.active}
              roomName={room.name}
              handleFullscreenRequest={handleFullscreenRequest}
              onRequestPopout={onRequestPopout}
              appointment={appointment}
              onRequestHangup={onRequestHangup}
              otherPartyName={otherPartyName}
              remoteParticipants={remoteParticipants}
              overlayMessages={fullPage || isPoppedOut}
              isTestMode={true}
            >
              {null}
            </AppointmentVideo>
          )}
        </FullScreen>
      </VideoColumn>
    </RoomDataProvider>
  );
}
